import React, { useEffect, useRef, useState } from "react";
import "./ProofWindow.css";

/**
 * ProofWindow — draggable, closable floating card. Used on the mirror
 * display to show "proof" of whatever R.A.W. is talking about (messages,
 * positions, balance, finance, etc.) as it speaks the brief.
 */
export default function ProofWindow({
  id, title, accent = "gold", x = 60, y = 60, width = 360, onClose, onFocus, onMoved, zIndex = 10, children,
}) {
  const cardRef = useRef(null);
  const [pos, setPos] = useState({ x, y });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0, moved: false });

  // Reflect prop changes (e.g. parent restored a saved position) into local state
  useEffect(() => { setPos({ x, y }); }, [x, y]);

  // Keep within viewport on resize
  useEffect(() => {
    const clamp = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      setPos((p) => ({
        x: Math.max(8, Math.min(vw - width - 8, p.x)),
        y: Math.max(8, Math.min(vh - 60, p.y)),
      }));
    };
    window.addEventListener("resize", clamp);
    return () => window.removeEventListener("resize", clamp);
  }, [width]);

  const onMouseDown = (e) => {
    if (e.target.closest(".proof-win-close")) return;
    dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, baseX: pos.x, baseY: pos.y, moved: false };
    onFocus?.(id);
    document.body.style.userSelect = "none";
  };
  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d.active) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (Math.abs(dx) + Math.abs(dy) > 2) d.moved = true;
      setPos({
        x: Math.max(8, Math.min(window.innerWidth - width - 8, d.baseX + dx)),
        y: Math.max(8, Math.min(window.innerHeight - 60, d.baseY + dy)),
      });
    };
    const onUp = () => {
      const d = dragRef.current;
      if (d.active) {
        d.active = false;
        document.body.style.userSelect = "";
        // If the card was actually dragged (not just clicked), persist its
        // new position via the parent's callback. ProofCanvas will save it
        // to localStorage so the brief restores this layout next time.
        if (d.moved) {
          setPos((p) => { onMoved?.(id, p.x, p.y); return p; });
        }
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [width, id, onMoved]);

  return (
    <div
      ref={cardRef}
      className={`proof-window accent-${accent}`}
      style={{ left: pos.x, top: pos.y, width, zIndex }}
      data-testid={`proof-window-${id}`}
      onMouseDown={onMouseDown}
    >
      <div className="proof-win-header">
        <span className="proof-win-dot" />
        <span className="proof-win-title">{title}</span>
        <button
          type="button"
          className="proof-win-close"
          onClick={(e) => { e.stopPropagation(); onClose?.(id); }}
          data-testid={`proof-window-close-${id}`}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div className="proof-win-body">{children}</div>
    </div>
  );
}
